import type { ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@/gql';

export const FetchCompanyDetails = graphql(`
    query FetchCompanyDetails($id: ObjectID!) {
        company(id: $id) {
            ...CompanyItemData
        }
    }
`);

const CompanyItemData = graphql(`
    fragment CompanyItemData on Company {
        id
        name
        shortName
        isActive
        isDemo
        createdAt
        contactInfo {
            name
            email
            phone
            address
        }
        subscriptions {
            durationMonths
            freeTrialDays
            extensionDays
            effectiveStartDate
            effectiveEndDate
            startDate
            endDate
            maxUserLicenses
            usedLicenses
            renewalReminderDays
        }
        settings {
            dataRetentionPeriod
            notifications {
                userRegistrationMode
                reportAvailabilityNotification
                emailSenderName
                welcomeEmailText
                reportAvailabilityEmailText
            }
            enforceOtp2FA
        }
        roleSettings {
            role
            customName
            isEnabled
        }
        enabledModules
    }
`);

export const FetchAvailableModules = graphql(`
    query FetchAvailableModules(
        $filter: ModuleDefinitionFilteringRule
        $pagination: Pagination
        $sort: ModuleDefinitionSortingRule
    ) {
        availableModules(filter: $filter, pagination: $pagination, sort: $sort) {
            count
            items {
                ... on AssessmentModuleDefinition {
                    __typename
                    ...AssessmentModuleFields
                }
                ... on TrainingModuleDefinition {
                    __typename
                    ...TrainingModuleFields
                }
                ... on ExternalModuleDefinition {
                    __typename
                    ...ExternalModuleFields
                }
                ... on NutritionModuleDefinition {
                    __typename
                    ...NutritionModuleFields
                }
                ... on ReportingModuleDefinition {
                    __typename
                    ...ReportingModuleFields
                }
            }
        }
    }
`);

export const FetchAvailableModulesByCategory = graphql(`
    query FetchAvailableModulesByCategory($pagination: Pagination, $sort: ModuleDefinitionSortingRule) {
        assessmentModules: availableModules(
            filter: { categories: [Assessment] }
            pagination: $pagination
            sort: $sort
        ) {
            items {
                ... on AssessmentModuleDefinition {
                    __typename
                    ...AssessmentModuleFields
                }
            }
        }

        trainingModules: availableModules(filter: { categories: [Training] }, pagination: $pagination, sort: $sort) {
            items {
                ... on TrainingModuleDefinition {
                    __typename
                    ...TrainingModuleFields
                }
            }
        }

        nutritionModules: availableModules(filter: { categories: [Nutrition] }, pagination: $pagination, sort: $sort) {
            items {
                ... on NutritionModuleDefinition {
                    __typename
                    ...NutritionModuleFields
                }
            }
        }

        reportingModules: availableModules(filter: { categories: [Reporting] }, pagination: $pagination, sort: $sort) {
            items {
                ... on ReportingModuleDefinition {
                    __typename
                    ...ReportingModuleFields
                }
            }
        }

        externalModules: availableModules(filter: { categories: [External] }, pagination: $pagination, sort: $sort) {
            items {
                ... on ExternalModuleDefinition {
                    __typename
                    ...ExternalModuleFields
                }
            }
        }
    }
`);

const AssessmentModuleFields = graphql(`
    fragment AssessmentModuleFields on AssessmentModuleDefinition {
        id
        identifier
        name
        category
        subCategory
        levelOrVariant
        tags
        isActive
    }
`);

const TrainingModuleFields = graphql(`
    fragment TrainingModuleFields on TrainingModuleDefinition {
        id
        identifier
        name
        category
        subCategory
        levelOrVariant
        tags
        isActive
    }
`);

const ExternalModuleFields = graphql(`
    fragment ExternalModuleFields on ExternalModuleDefinition {
        id
        identifier
        name
        category
        subCategory
        isActive
    }
`);

const NutritionModuleFields = graphql(`
    fragment NutritionModuleFields on NutritionModuleDefinition {
        id
        identifier
        name
        category
        isActive
    }
`);

const ReportingModuleFields = graphql(`
    fragment ReportingModuleFields on ReportingModuleDefinition {
        id
        identifier
        name
        category
        isActive
    }
`);

export type FetchAvailableModulesQuery = ResultOf<typeof FetchAvailableModules>;

type CompanyItemDataFragment = ResultOf<typeof CompanyItemData>;
export type CompanyRoleSetting = CompanyItemDataFragment['roleSettings'][number];
